import { FC, useState, useEffect } from "react"
import { verifyCode } from '@utils/api'
import { useTranslation } from "react-i18next";
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Button } from '@/components/ui/button';

type EndpointVerificationProps = {
  platform: string
  endpoint: string
  onSuccess: () => void
  onSendVerificationCode?: () => Promise<void>
  onBack?: () => void
  autoSendCode?: boolean // If true, automatically sends code on mount
}

const EndpointVerification: FC<EndpointVerificationProps> = ({ platform, endpoint, onSuccess, onSendVerificationCode, onBack, autoSendCode = false }) => {
  const { t } = useTranslation();
  const [verificationErrorCode, setVerificationErrorCode] = useState(0)
  const [code, setCode] = useState('')
  const [isConfirmed, setIsConfirmed] = useState(autoSendCode)
  const [isCodeSent, setIsCodeSent] = useState(autoSendCode)
  const [resendTimer, setResendTimer] = useState(autoSendCode ? 60 : 0)
  const [isLoading, setIsLoading] = useState(false)
  
  // Timer effect for resend functionality
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(timer => timer - 1)
      }, 1000)
    } else if (interval) {
      clearInterval(interval)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [resendTimer])

  // Auto-send verification code on mount if autoSendCode is true
  useEffect(() => {
    if (autoSendCode && onSendVerificationCode) {
      onSendVerificationCode().catch((error) => {
        console.error('Failed to auto-send verification code:', error)
      })
    }
  }, [autoSendCode, onSendVerificationCode])

  const handleConfirmEndpoint = async () => {
    setIsLoading(true)
    try {
      if (onSendVerificationCode) {
        await onSendVerificationCode()
      }
      setIsConfirmed(true)
      setIsCodeSent(true)
      setResendTimer(60)
    } catch (error) {
      console.error('Failed to send verification code:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setIsLoading(true)
    setCode('')
    setVerificationErrorCode(0)
    try {
      if (onSendVerificationCode) {
        await onSendVerificationCode()
      }
      setResendTimer(60)
    } catch (error) {
      console.error('Failed to resend verification code:', error)
    } finally {
      setIsLoading(false)
    }
  }
  
  const submitCode = (value: string) => {
    verifyCode(endpoint, platform, value).then((result) => onSuccess()).catch((err) => {
      setCode('')
      setVerificationErrorCode(err.status)
    })
  }

  // Endpoint confirmation step
  if (!isConfirmed) {
    return (
      <div className="my-8">
        <div className="my-8 space-y-4 text-center">
          <h2 className="text-lg font-medium">
            {platform === 'email' ? t('verification.confirmEmail') : t('verification.confirmPhone')}
          </h2>
          <p className="text-gray-600">
            {t('verification.sendToPrompt')}
          </p>
          <p className="text-lg font-medium">{endpoint}</p>
          <p className="text-sm text-gray-500">
            {t('verification.isCorrect')}
          </p>
        </div>

        <div className="flex justify-center space-x-4">
          <Button
            variant="outline"
            onClick={onBack}
          >
            {t('verification.change')}
          </Button>
          <Button
            onClick={handleConfirmEndpoint}
            disabled={isLoading}
          >
            {isLoading ? t('verification.sending') : t('verification.sendCode')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="my-8">
      <div className="my-8 space-y-4 text-center">
        <h2 className="text-lg font-medium">{t('booking.verificationCode.sent', {endpoint: endpoint})}</h2>
        <p>{t('booking.verificationCode.instructions')}</p>
      </div>
      
      <div className="space-y-4">
        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={(value) => {
              setCode(value)
              if(verificationErrorCode != 0 && value.length < 6){
                setVerificationErrorCode(0)
              }
            }}
            onComplete={submitCode}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
        
        {verificationErrorCode != 0 && (
          <p className="text-sm text-destructive text-center">
            {t('verification.invalidCode')}
          </p>
        )}

        <div className="flex justify-center">
          {resendTimer > 0 ? (
            <p className="text-sm text-gray-500">
              {t('verification.resendIn', { seconds: resendTimer })}
            </p>
          ) : (
            <Button
              variant="ghost"
              onClick={handleResendCode}
              disabled={isLoading}
              className="text-sm"
            >
              {isLoading ? t('verification.sending') : t('verification.resendCode')}
            </Button>
          )}
        </div>
      </div>
      
      {platform === 'email' ? (
        <p className="mt-8 text-sm text-gray-600">
          {t('booking.verificationCode.checkSpamFolder')}
        </p>
      ) : null}
    </div>

  )
}

export default EndpointVerification